import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import Logo from "@/components/Logo";
import { Menu, X } from "lucide-react";
import { SIGN_IN_URL, SIGN_UP_URL } from "./config";
import { trackCta } from "./hooks/useMarketingAnalytics";

const NAV = [
  { to: "/features", label: "Features" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/integrations", label: "Integrations" },
  { to: "/resources", label: "Resources" },
];

export default function MarketingNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="marketing-nav"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur border-b border-[#E8E2D9]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/home" aria-label="ClientDesk VA home" className="flex items-center">
          <Logo variant="compact" size={30} />
        </Link>

        <ul className="hidden lg:flex items-center gap-1 text-[14px] font-medium text-[#1A2A3A]">
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink
                to={n.to}
                data-testid={`marketing-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full transition-colors ${
                    isActive ? "text-[#E8712C]" : "hover:text-[#E8712C]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-2">
          <a
            href={SIGN_IN_URL}
            data-testid="marketing-nav-signin"
            onClick={() => trackCta("nav_signin")}
            className="px-4 py-2 text-[14px] font-medium text-[#1A2A3A] rounded-full hover:text-[#E8712C]"
          >
            Sign in
          </a>
          <a
            href={SIGN_UP_URL}
            data-testid="marketing-nav-getstarted"
            onClick={() => trackCta("nav_getstarted")}
            className="px-5 py-2.5 text-[14px] font-semibold bg-[#E8712C] hover:bg-[#C55A1F] text-white rounded-full transition-colors"
          >
            Get started
          </a>
        </div>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2 text-[#1A2A3A]"
          data-testid="marketing-nav-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-[#E8E2D9] bg-white" data-testid="marketing-nav-mobile-panel">
          <ul className="px-5 py-4 space-y-1">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="block px-3 py-2.5 text-[15px] text-[#1A2A3A] rounded-lg hover:bg-[#FAF7F2]"
                >
                  {n.label}
                </NavLink>
              </li>
            ))}
            <li className="pt-2 flex gap-2">
              <a
                href={SIGN_IN_URL}
                onClick={() => trackCta("nav_signin_mobile")}
                className="flex-1 text-center px-4 py-2.5 text-[14px] font-medium text-[#1A2A3A] border border-[#E8E2D9] rounded-full"
              >
                Sign in
              </a>
              <a
                href={SIGN_UP_URL}
                onClick={() => trackCta("nav_getstarted_mobile")}
                className="flex-1 text-center px-4 py-2.5 text-[14px] font-semibold bg-[#E8712C] text-white rounded-full"
              >
                Get started
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
